import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlus, Eye, SquarePen, RefreshCw, FileText, PenLine, Clock, CheckCircle2, Check, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { issueNoteService } from '../../services/issueNoteService';
import { IssueNoteListDTO, IssueNoteStatus } from '../../types';
import { Permissions } from '../../constants/Permissions';
import { useAuth } from '../../context/AuthContext';
import './IssueNotePage.css';

const STATUS_CLASS: Record<IssueNoteStatus, string> = {
  Draft:           'ims-badge isn-badge-draft',
  PendingApproval: 'ims-badge isn-badge-pending',
  Approved:        'ims-badge isn-badge-approved',
  Rejected:        'ims-badge isn-badge-rejected',
  Posted:          'ims-badge isn-badge-posted',
  Cancelled:       'ims-badge isn-badge-cancelled',
};

const STATUS_LABEL: Record<IssueNoteStatus, string> = {
  Draft:           'Draft',
  PendingApproval: 'Pending Approval',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
};

const IssueNoteListPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();
  const { hasPermission } = useAuth();

  const [items,      setItems]      = useState<IssueNoteListDTO[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [flash,      setFlash]      = useState<string | null>((location.state as any)?.message ?? null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => n.status !== 'Posted' && n.status !== 'Cancelled'));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => n.status !== 'Posted' && n.status !== 'Cancelled'));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (flash) { const tid = setTimeout(() => setFlash(null), 5000); return () => clearTimeout(tid); }
  }, [flash]);

  // ── Stat counts ────────────────────────────────────────────────────────────
  const totalCount    = items.length;
  const draftCount    = items.filter(n => n.status === 'Draft').length;
  const pendingCount  = items.filter(n => n.status === 'PendingApproval').length;
  const approvedCount = items.filter(n => n.status === 'Approved').length;

  const columns: GridColumn[] = [
    {
      key: 'issueNoteNumber',
      label: t('issueNote.issueNoteNumber', 'ISN Number'),
      sortable: true,
      width: '160px',
      render: (v) => <code>{v as string}</code>,
    },
    {
      key: 'issueDate',
      label: t('issueNote.issueDate', 'Issue Date'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    {
      key: 'warehouseName',
      label: t('issueNote.warehouse', 'Warehouse'),
      sortable: true,
      width: '160px',
    },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      sortable: true,
      width: '140px',
      render: (v) => {
        const s = v as IssueNoteStatus;
        return <span className={STATUS_CLASS[s] ?? 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    {
      key: 'itemCount',
      label: t('issueNote.items', 'Items'),
      width: '80px',
    },
    {
      key: 'issuedByName',
      label: t('issueNote.issuedBy', 'Issued By'),
      width: '140px',
    },
    {
      key: 'createdAt',
      label: t('common.createdAt', 'Created'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('common.view', 'View'),
      onClick: (row) => navigate(`/issue-notes/${row.id}`),
      className: 'grid-action-btn-view',
    },
    ...(hasPermission(Permissions.IssueNotes.Edit) ? [{
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      title: t('common.edit', 'Edit'),
      onClick: (row: IssueNoteListDTO) => navigate(`/issue-notes/${row.id}/edit`),
      className: 'grid-action-btn-edit',
      hidden: (row: IssueNoteListDTO) => row.status !== 'Draft',
    }] : []),
  ];

  return (
    <Layout>
      <div className="isn-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('issueNote.title', 'Issue Notes')}</h1>
          <div className="ims-list-actions">
            <PermissionGuard claim={Permissions.IssueNotes.Create}>
              <button className="btn-new" onClick={() => navigate('/issue-notes/create')}>
                <CirclePlus />
                {t('issueNote.createIssueNote', 'New Issue Note')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh', 'Refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh', 'Refresh')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-total">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalCount}</span>
              <span className="ims-stat-label">{t('issueNote.title', 'Issue Notes')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-draft">
              <PenLine size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{draftCount}</span>
              <span className="ims-stat-label">{t('issueNote.draft', 'Draft')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-pending">
              <Clock size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">{t('issueNote.pendingApproval', 'Pending Approval')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-posted">
              <CheckCircle2 size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{approvedCount}</span>
              <span className="ims-stat-label">{t('issueNote.approved', 'Approved')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {flash && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{flash}</span>
            <button type="button" onClick={() => setFlash(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={items}
            loading={loading}
            actions={actions}
            pageSize={10}
            searchPlaceholder={t('common.search', 'Search…')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={t('issueNote.noIssueNotes', 'No issue notes found. Create your first issue note.')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default IssueNoteListPage;
